import {Component, OnInit} from '@angular/core';
import {AppState} from './reducers';
import {select, Store} from '@ngrx/store';
import {Observable} from 'rxjs';
import {MatIconRegistry} from '@angular/material/icon';
import {DomSanitizer} from '@angular/platform-browser';
import {selectLoginState} from './auth/login/selectors/login.selectors';
import {loginFeatureKey, State} from './auth/login/reducers/login.reducer';
import {CookiesLogin, LogoutUser} from './auth/login/actions/login.actions';
import * as moment from 'moment';
import {NavigationEnd, Router} from '@angular/router';
import {NgcCookieConsentService} from 'ngx-cookieconsent';
import {map} from 'rxjs/operators';
import {DashboardService} from './dashboard/dashboard.service';
import {getNotifications} from './reducers/notification/notification.actions';
import {cookies} from './header/cookies/accept-terms-mobile.component';
import {MatDialog} from '@angular/material/dialog';
import {CognitoService} from './cognito.service';
import {TranslateService} from '@ngx-translate/core';
import {LanguageService} from './services/language.service';
import {LocaleService} from './services/locale.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit {
  title = 'frontend';
  user$: Observable<State>;
  contactMethod: string;
  menuOpen: boolean;
  login$: Observable<any>;
  notifications$: Observable<any[]>;
  isAuthenticated: boolean;
  stellantisPageRouteActive: boolean;
  isLogin = false;

  constructor(
    private ccService: NgcCookieConsentService,
    private store: Store<AppState>,
    private router: Router,
    private dashboardService: DashboardService,
    private matIconRegistry: MatIconRegistry,
    private domSanitizer: DomSanitizer,
    private dialog: MatDialog,
    private cognitoService: CognitoService,
    private translate: TranslateService,
    private languageService: LanguageService,
    private localeService: LocaleService
  ) {
    // Initialize language using the service
    this.languageService.init();
    this.localeService.start();   // react to currency → effective language, site-wide
    this.isAuthenticated = false;

    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.stellantisPageRouteActive = event.urlAfterRedirects.includes('/stellantis');
        this.isLogin = event.urlAfterRedirects.startsWith('/auth/login');
      }
    });

    this.matIconRegistry.addSvgIcon(
      `fi_cargo`,
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/icons/misc/cargo-ship.svg')
    );
    this.matIconRegistry.addSvgIcon(
      `fi_fax`,
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/icons/misc/fax.svg')
    );
    this.matIconRegistry.addSvgIcon(
      `fi_land_phone`,
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/icons/misc/lan-phone.svg')
    );
    this.matIconRegistry.addSvgIcon(
      `fi_map`,
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/icons/misc/map.svg')
    );
    this.matIconRegistry.addSvgIcon(
      `fi_email`,
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/icons/misc/email.svg')
    );
    this.matIconRegistry.addSvgIcon(
      `fi_user`,
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/icons/misc/user.svg')
    );
    this.matIconRegistry.addSvgIcon(
      `fi_user_login`,
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/icons/misc/user.svg')
    );
    this.matIconRegistry.addSvgIcon(
      `fi_user_forget`,
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/icons/misc/thinking.svg')
    );
    this.matIconRegistry.addSvgIcon(
      `fi_smartphone`,
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/icons/misc/smartphone.svg')
    );
    this.matIconRegistry.addSvgIcon(
      `fi_key`,
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/icons/misc/key.svg')
    );
    this.matIconRegistry.addSvgIcon(
      `fi_audi`,
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/icons/misc/ff_audi.svg')
    );
    this.matIconRegistry.addSvgIcon(
      `fi_cupload`,
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/icons/misc/cloud-computing.svg')
    );
    this.matIconRegistry.addSvgIcon(
      `fi_doc`,
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/icons/misc/document.svg')
    );
    this.matIconRegistry.addSvgIcon(
      `fi_mdoc`,
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/icons/misc/multi_doc.svg')
    );
    this.matIconRegistry.addSvgIcon(
      `fi_lux`,
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/icons/misc/luxury.svg')
    );
    this.matIconRegistry.addSvgIcon(
      `fi_barcode`,
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/icons/misc/bar-code.svg')
    );
    this.matIconRegistry.addSvgIcon(
      `fi_settings`,
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/icons/misc/settings.svg')
    );
    this.matIconRegistry.addSvgIcon(
      `fi_import`,
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/icons/misc/import.svg')
    );
    this.matIconRegistry.addSvgIcon(
      `fi_inventory`,
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/icons/misc/inventory.svg')
    );
    this.matIconRegistry.addSvgIcon(
      `fi_phone`,
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/icons/misc/smartphone.svg')
    );
    this.matIconRegistry.addSvgIcon(
      `fi_delivery`,
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/icons/misc/delivery.svg')
    );
    this.matIconRegistry.addSvgIcon(
      `fi_eye`,
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/icons/misc/view.svg')
    );
    this.matIconRegistry.addSvgIcon(
      `fi_pdf`,
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/icons/misc/pdf.svg')
    );
    this.matIconRegistry.addSvgIcon(
      `fi_whatsapp`,
      this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/icons/misc/whatsapp.svg')
    );
    this.store.dispatch(CookiesLogin());
  }


  ngOnInit(): void {
    console.log('version 2.0.0');

    this.cognitoService.isAuthenticated()
      .then((success: boolean) => {
        this.isAuthenticated = success;
      });

    const now = moment();
    const hour = now.get('hour');
    if (hour > 8 && hour < 18) {
      this.contactMethod = 'wa';
    } else {
      this.contactMethod = 'mail';
    }
    this.user$ = this.store.pipe(select(selectLoginState)).pipe(map(state => {
      return state;
    }));

    this.translate.onLangChange.subscribe(() => this.applyCookieLanguage());
    if (this.translate.currentLang) { this.applyCookieLanguage(); }

    // Mark the <body> for non-Israeli-market users so global CSS can hide
    // Israel-only chrome (e.g. the accessibility widget) regardless of when
    // that third-party element is injected.
    this.localeService.israeliMarket$.subscribe(isIsraeli => {
      document.body.classList.toggle('non-israeli-market', !isIsraeli);
    });
  }

  /** Apply the cookie-consent banner text in the active UI language, then re-init the popup. */
  private applyCookieLanguage(): void {
    const config = this.ccService.getConfig();
    if (!config || !config.content) { return; }
    config.content.message = this.translate.instant('COOKIE.MESSAGE');
    config.content.dismiss = this.translate.instant('COOKIE.DISMISS');
    try {
      this.ccService.destroy();
      this.ccService.init(config);
    } catch (e) {
      console.error('cookie-consent re-init failed', e);
    }
  }

  get isUAERoute(): boolean {
    return this.router.url.startsWith('/uae') || this.router.url.startsWith('/ga');
  }

  contact() {

  }

  menuClicked($event: boolean) {
    this.menuOpen = !this.menuOpen;
  }

  onBackDropClick() {

  }

  navigateWa() {
    window.location.href = 'https://wa.me/972506880102';
  }

  navigateMail() {
    window.location.href = 'mailto:eladbarshan@gmail.com';
  }

  logout($event: boolean) {
    this.store.dispatch(LogoutUser());
  }

  openDialog() {

    const approve = this.dialog.open(cookies, {});
  }

  public signOut(): void {
    this.cognitoService.signOut()
      .then(() => {
        this.router.navigate(['/signIn']);
      });
  }
}
